import React, { useState } from "react";
import PropTypes from "prop-types";
import ResourceTypeIcon from "../ResourceTypeIcon";
import styles from "./ResourceItem.module.css";
import Modal from "components/Modal";
import Button from "components/Button";
import downloadIcon from "assets/download_Icon.svg";
import mobile from "mobileBridge";
import { useTranslation } from "react-i18next";

export function ResourceItem({ format, title, date, path, downloadPath }) {
  const {t} = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const openDialog = () => {
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleIconClick = () => {
    // PDF, DOC, PPT, PPS, Keynote, Url
    if ([1, 4, 5, 6, 7, 8].includes(format)) {
      if (mobile.isApp()) {
        mobile.openLink(path);
      } else {
        window.open(path, "_blank");
      }
      // Audio, Video
    } else if ([2, 3].includes(format)) {
      openDialog();
    }
  };

  // Helper function to handle blob downloading
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleFileDownload = async () => {
    if (mobile.isApp()) {
      mobile.openLink(downloadPath);
    } else {
      try {
        // Fetch the file to inspect its content type
        const response = await fetch(downloadPath);
        if (!response.ok) throw new Error(t("Network response was not ok"));

        const contentType = response.headers.get("Content-Type");
        const blob = await response.blob();

        let fileExtension;
        if (contentType === "application/pdf") {
          fileExtension = "pdf";
        } else {
          fileExtension = getFileExtension(format);
        }

        downloadBlob(blob, `${title}.${fileExtension}`);
      } catch (error) {
        console.error(t("Error handling the file download"), error);
      }
    }
  };

  const getFileExtension = (format) => {
    switch (format) {
      case 1:
        return "pdf";
      case 2:
        return "mp3";
      case 3:
        return "mp4";
      case 4:
        return "doc";
      case 5:
        return "ppt";
      case 6:
        return "pps";
      case 7:
        return "key";
      case 8:
        return "";
      default:
        return "";
    }
  };

  const renderContent = () => {
    // Video
    if (format === 3) {
      return (
        <video controls width="100%" height="450px">
          <source src={downloadPath} type="video/mp4" />
        </video>
      );
      // Audio
    } else if (format === 2) {
      return (
        <audio controls>
          <source src={downloadPath} type="audio/mp3" />
        </audio>
      );
    } else {
      return null;
    }
  };

  const getFormatLabel = (format) => {
    switch (format) {
      case 1:
        return "PDF";
      case 2:
        return "Audio";
      case 3:
        return "Video";
      case 4:
        return "DOC";
      case 5:
        return "PPT";
      case 6:
        return "PPS";
      case 7:
        return "Keynote";
      case 8:
        return "Url";
      default:
        return "Undefined";
    }
  };

  const formatLabel = getFormatLabel(format);

  return (
    <div className={styles.container}>
      <div className={styles.resourceItem}>
        <div className={styles.content}>
          <span className={styles.label}>{title}</span>
          <span className={styles.type}>{formatLabel}</span>
          <span className={styles.date}>{date}</span>
        </div>
        <div className={styles.iconsContainer}>
          <ResourceTypeIcon
            format={format}
            onClick={handleIconClick}
            className={styles.icon}
          />

          <img
            src={downloadIcon}
            alt="icon"
            onClick={handleFileDownload}
            className={styles.icon}
          />
        </div>
      </div>

      {isDialogOpen && (
        <Modal isOpen={isDialogOpen}>
          <div className={styles.dialogContent}>
            {renderContent()}
            <div className={styles.buttonContainer}>
              <Button buttonText={t("Close")} onClick={closeDialog} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

ResourceItem.propTypes = {
  title: PropTypes.string,
  format: PropTypes.number,
  date: PropTypes.string,
  path: PropTypes.string,
  downloadPath: PropTypes.string,
};
